import { collection, getDocs, query, orderBy, limit, doc, setDoc, getDoc, where } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { Contractor } from '../types/contractor.ts';

export interface InvoiceNumber {
  id?: string;
  invoiceNumber: string;
  companyPrefix: string; // 'CEC', 'REL', 'BTC', etc.
  fullInvoiceNumber: string; // e.g., 'REL-1001' or '1001' for CEC
  createdAt: Date;
  jobNumber?: string;
  jobName?: string;
}

export interface CompanyInvoiceSettings {
  id: string;
  companyPrefix: string;
  companyName: string;
  lastInvoiceNumber: number;
  updatedAt: Date;
}

class InvoiceNumberingService {
  private static instance: InvoiceNumberingService;
  private contractorsCache: Contractor[] = [];
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  public static getInstance(): InvoiceNumberingService {
    if (!InvoiceNumberingService.instance) {
      InvoiceNumberingService.instance = new InvoiceNumberingService();
    }
    return InvoiceNumberingService.instance;
  }

  /**
   * Loads active contractors with invoicePrefix from the database
   * @returns Promise<Contractor[]> - Array of active contractors
   */
  private async loadActiveContractors(): Promise<Contractor[]> {
    // Return cached data if still valid
    if (Date.now() < this.cacheExpiry && this.contractorsCache.length > 0) {
      return this.contractorsCache;
    }

    try {
      const contractorsRef = collection(db, 'contractors');
      const contractorsQuery = query(
        contractorsRef, 
        where('status', '==', 'active'),
        orderBy('companyName')
      );
      const snapshot = await getDocs(contractorsQuery);
      
      const contractors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        licenseExpires: doc.data().licenseExpires?.toDate(),
        insuranceExpires: doc.data().insuranceExpires?.toDate(),
        bondExpires: doc.data().bondExpires?.toDate(),
      })) as Contractor[];

      // Cache the results
      this.contractorsCache = contractors;
      this.cacheExpiry = Date.now() + this.CACHE_DURATION;
      
      return contractors;
    } catch (error) {
      console.error('Error loading active contractors:', error);
      return [];
    }
  }

  /**
   * Gets all unique invoice prefixes from active contractors
   * @returns Promise<string[]> - Array of unique invoice prefixes
   */
  private async getActiveInvoicePrefixes(): Promise<string[]> {
    const contractors = await this.loadActiveContractors();
    const prefixes = new Set<string>();
    
    // Always include CEC as default
    prefixes.add('CEC');
    
    contractors.forEach(contractor => {
      if (contractor.invoicePrefix && contractor.invoicePrefix.trim()) {
        prefixes.add(contractor.invoicePrefix.trim().toUpperCase());
      }
    });
    
    return Array.from(prefixes).sort();
  }

  /**
   * Gets contractor info by invoice prefix
   * @param invoicePrefix - The invoice prefix to look up
   * @returns Promise<{name: string, prefix: string} | null> - Contractor info or null
   */
  private async getContractorByPrefix(invoicePrefix: string): Promise<{name: string, prefix: string} | null> {
    const contractors = await this.loadActiveContractors();
    
    // Handle CEC special case
    if (invoicePrefix === 'CEC' || !invoicePrefix) {
      return { name: 'CEC Facilities Services', prefix: 'CEC' };
    }
    
    const contractor = contractors.find(c => 
      c.invoicePrefix && c.invoicePrefix.trim().toUpperCase() === invoicePrefix.toUpperCase()
    );
    
    return contractor ? { name: contractor.companyName, prefix: contractor.invoicePrefix } : null;
  }

  /**
   * Gets the next available invoice number for a company
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @returns Promise<string> - The next invoice number (e.g., 'REL-1002' or '1002' for CEC)
   */
  async getNextInvoiceNumber(companyPrefix: string): Promise<string> {
    try {
      // Always scan existing invoices to get the most up-to-date highest number
      console.log(`Getting next invoice number for ${companyPrefix}...`);
      const highestExisting = await this.scanExistingInvoices(companyPrefix);
      
      // Get or create company invoice settings
      const settings = await this.getOrCreateCompanySettings(companyPrefix);
      
      // Use the higher of the two: settings or scanned existing
      const currentHighest = Math.max(settings.lastInvoiceNumber, highestExisting);
      console.log(`Current highest for ${companyPrefix}: ${currentHighest} (settings: ${settings.lastInvoiceNumber}, scanned: ${highestExisting})`);
      
      // Increment the invoice number
      const nextNumber = currentHighest + 1;
      
      // Format the invoice number based on company prefix
      let formattedNumber: string;
      if (companyPrefix === 'CEC') {
        formattedNumber = nextNumber.toString();
      } else {
        formattedNumber = `${companyPrefix}-${nextNumber}`;
      }
      
      console.log(`Next invoice number for ${companyPrefix}: ${formattedNumber}`);
      return formattedNumber;
    } catch (error) {
      console.error('Error getting next invoice number:', error);
      throw new Error(`Failed to get next invoice number for ${companyPrefix}`);
    }
  }

  /**
   * Reserves an invoice number and saves it to the database
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @param jobNumber - Optional job number
   * @param jobName - Optional job name
   * @returns Promise<InvoiceNumber> - The reserved invoice number record
   */
  async reserveInvoiceNumber(
    companyPrefix: string, 
    jobNumber?: string, 
    jobName?: string
  ): Promise<InvoiceNumber> {
    try {
      // For reserving, also prioritize actual saved invoices to avoid gaps
      console.log(`Reserving invoice number for ${companyPrefix}...`);
      const highestExisting = await this.scanExistingInvoices(companyPrefix);
      const nextNumber = highestExisting + 1;
      
      // Format the invoice number based on company prefix
      let invoiceNumber: string;
      if (companyPrefix === 'CEC') {
        invoiceNumber = nextNumber.toString();
      } else {
        invoiceNumber = `${companyPrefix}-${nextNumber}`;
      }
      
      // Extract the numeric part
      const numericPart = companyPrefix === 'CEC' 
        ? parseInt(invoiceNumber) 
        : parseInt(invoiceNumber.split('-')[1]);
      
      // Create the invoice number record
      const invoiceRecord: InvoiceNumber = {
        invoiceNumber: numericPart.toString(),
        companyPrefix,
        fullInvoiceNumber: invoiceNumber,
        createdAt: new Date(),
        jobNumber,
        jobName
      };
      
      // Save to invoiceNumbers collection
      const invoiceRef = doc(collection(db, 'invoiceNumbers'));
      await setDoc(invoiceRef, {
        ...invoiceRecord,
        id: invoiceRef.id
      });
      
      // Update company settings with the new last invoice number
      await this.updateCompanyLastInvoiceNumber(companyPrefix, numericPart);
      
      return {
        ...invoiceRecord,
        id: invoiceRef.id
      };
    } catch (error) {
      console.error('Error reserving invoice number:', error);
      throw new Error(`Failed to reserve invoice number for ${companyPrefix}`);
    }
  }

  /**
   * Gets the current highest invoice number for a company by scanning existing invoices
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @returns Promise<number> - The highest invoice number found
   */
  private async scanExistingInvoices(companyPrefix: string): Promise<number> {
    try {
      console.log(`Scanning existing invoices for ${companyPrefix}...`);
      
      let highestNumber = companyPrefix === 'CEC' ? 1000 : 1000; // Default starting numbers
      
      // First, scan the main invoices collection for existing invoice numbers
      try {
        console.log('Scanning main invoices collection...');
        const mainInvoicesRef = collection(db, 'invoices');
        
        // Try to get all invoices without ordering in case createdAt field doesn't exist on all documents
        let mainSnapshot;
        try {
          // Try with ordering first
          const mainInvoicesQuery = query(
            mainInvoicesRef,
            orderBy('createdAt', 'desc'),
            limit(200)
          );
          mainSnapshot = await getDocs(mainInvoicesQuery);
        } catch (orderError) {
          console.log('Could not order by createdAt, getting all documents...');
          // If ordering fails, just get all documents
          mainSnapshot = await getDocs(mainInvoicesRef);
        }
        console.log(`Found ${mainSnapshot.docs.length} existing invoices in main collection`);
        
        let cecInvoices = [];
        
        mainSnapshot.docs.forEach(doc => {
          const data = doc.data();
          
          // Try multiple field names that might contain the invoice number
          let invoiceNumber = data.invoiceNumber || data.number || data.invNumber || data.invoice_number || '';
          
          if (invoiceNumber) {
            let numericPart = 0;
            const invoiceStr = invoiceNumber.toString();
            
            if (companyPrefix === 'CEC') {
              // For CEC: look for plain numbers or numbers without prefix
              if (!invoiceStr.includes('REL-') && !invoiceStr.includes('BTC-')) {
                // Extract all digits and take the largest number
                const matches = invoiceStr.match(/\d+/g);
                if (matches) {
                  numericPart = Math.max(...matches.map(match => parseInt(match, 10)));
                  if (numericPart >= 2800) { // Only log recent ones
                    cecInvoices.push({ number: numericPart, original: invoiceStr });
                  }
                }
              }
            } else {
              // For any other prefix: look for PREFIX- pattern
              if (invoiceStr.includes(`${companyPrefix}-`)) {
                const match = invoiceStr.match(new RegExp(`${companyPrefix}-(\\d+)`));
                if (match) {
                  numericPart = parseInt(match[1], 10);
                }
              }
            }
            
            if (numericPart && numericPart > highestNumber) {
              highestNumber = numericPart;
            }
          }
        });
        
        // Only log CEC invoices for debugging
        if (companyPrefix === 'CEC' && cecInvoices.length > 0) {
          console.log('Recent CEC invoices found:', cecInvoices.sort((a, b) => b.number - a.number));
        }
      } catch (mainError) {
        console.warn('Could not scan main invoices collection:', mainError);
      }
      
      // Skip scanning the tracking collection for now to avoid interference
      console.log('Skipping tracking collection scan to use only actual saved invoices');
      
      console.log(`Highest invoice number for ${companyPrefix}: ${highestNumber}`);
      return highestNumber;
    } catch (error) {
      console.error('Error scanning existing invoices:', error);
      const fallback = companyPrefix === 'CEC' ? 1000 : 1000;
      console.log(`Using fallback starting number for ${companyPrefix}: ${fallback}`);
      return fallback; // Fallback to default
    }
  }

  /**
   * Gets or creates company invoice settings
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @returns Promise<CompanyInvoiceSettings> - The company settings
   */
  private async getOrCreateCompanySettings(companyPrefix: string): Promise<CompanyInvoiceSettings> {
    try {
      const settingsRef = doc(db, 'companyInvoiceSettings', companyPrefix);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        return {
          id: settingsSnap.id,
          companyPrefix: data.companyPrefix,
          companyName: data.companyName,
          lastInvoiceNumber: data.lastInvoiceNumber,
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      } else {
        // Create new settings by scanning existing invoices
        const highestNumber = await this.scanExistingInvoices(companyPrefix);
        
        // Get contractor info dynamically
        const contractorInfo = await this.getContractorByPrefix(companyPrefix);
        const companyName = contractorInfo ? contractorInfo.name : companyPrefix;
        
        const newSettings: CompanyInvoiceSettings = {
          id: companyPrefix,
          companyPrefix,
          companyName,
          lastInvoiceNumber: highestNumber,
          updatedAt: new Date()
        };
        
        // Save the new settings
        await setDoc(settingsRef, newSettings);
        
        return newSettings;
      }
    } catch (error) {
      console.error('Error getting/creating company settings:', error);
      throw new Error(`Failed to get settings for ${companyPrefix}`);
    }
  }

  /**
   * Updates the last invoice number for a company
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @param lastInvoiceNumber - The new last invoice number
   */
  private async updateCompanyLastInvoiceNumber(companyPrefix: string, lastInvoiceNumber: number): Promise<void> {
    try {
      const settingsRef = doc(db, 'companyInvoiceSettings', companyPrefix);
      await setDoc(settingsRef, {
        lastInvoiceNumber,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating company last invoice number:', error);
      throw new Error(`Failed to update last invoice number for ${companyPrefix}`);
    }
  }

  /**
   * Gets all company invoice settings
   * @returns Promise<CompanyInvoiceSettings[]> - Array of all company settings
   */
  async getAllCompanySettings(): Promise<CompanyInvoiceSettings[]> {
    try {
      const settingsRef = collection(db, 'companyInvoiceSettings');
      const snapshot = await getDocs(settingsRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      } as CompanyInvoiceSettings));
    } catch (error) {
      console.error('Error getting all company settings:', error);
      return [];
    }
  }

  /**
   * Determines the company prefix based on employee rates or other business logic
   * @param employee - Employee object with rates
   * @returns string - The company prefix ('CEC', 'REL', 'BTC')
   */
  static determineCompanyPrefix(employee: any): string {
    // Business logic to determine which company this employee works for
    // Based on which rate is highest or set
    if (employee.rates?.btc && employee.rates.btc > 0) {
      return 'BTC';
    } else if (employee.rates?.reliable && employee.rates.reliable > 0) {
      return 'REL';
    } else {
      return 'CEC'; // Default to CEC
    }
  }

  /**
   * Peeks at the next invoice number without reserving it (for UI display)
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @returns Promise<string> - The next invoice number that would be assigned
   */
  async peekNextInvoiceNumber(companyPrefix: string): Promise<string> {
    try {
      // Scan existing invoices to get the most up-to-date highest number
      const highestExisting = await this.scanExistingInvoices(companyPrefix);
      
      // Get or create company invoice settings
      const settings = await this.getOrCreateCompanySettings(companyPrefix);
      
      // For preview purposes, prioritize the actual saved invoices over tracking records
      // This ensures we don't skip numbers due to reserved-but-never-saved invoices
      console.log(`Highest from actual saved invoices: ${highestExisting}`);
      console.log(`Settings last number: ${settings.lastInvoiceNumber} (from tracking)`);
      
      // Use the scanned existing invoices as the source of truth for previews
      const nextNumber = highestExisting + 1;
      
      // Format the invoice number based on company prefix
      let formattedNumber: string;
      if (companyPrefix === 'CEC') {
        formattedNumber = nextNumber.toString();
      } else {
        formattedNumber = `${companyPrefix}-${nextNumber}`;
      }
      
      return formattedNumber;
    } catch (error) {
      console.error('Error peeking at next invoice number:', error);
      const fallback = companyPrefix === 'CEC' ? '1001' : `${companyPrefix}-1001`;
      return fallback;
    }
  }

  /**
   * Gets the next invoice numbers for all companies (for UI display only - doesn't reserve)
   * @returns Promise<{[companyPrefix: string]: string}> - Next invoice numbers by company
   */
  async getNextInvoiceNumbers(): Promise<{[companyPrefix: string]: string}> {
    try {
      const activePrefixes = await this.getActiveInvoicePrefixes();
      const nextNumbers: {[companyPrefix: string]: string} = {};
      
      for (const prefix of activePrefixes) {
        const nextNumber = await this.peekNextInvoiceNumber(prefix);
        nextNumbers[prefix] = nextNumber;
      }
      
      return nextNumbers;
    } catch (error) {
      console.error('Error getting next invoice numbers:', error);
      // Fallback with basic prefixes
      return { CEC: '1001' };
    }
  }

  /**
   * Gets invoice history for a company
   * @param companyPrefix - The company prefix ('CEC', 'REL', 'BTC')
   * @param limitCount - Number of records to return (default: 10)
   * @returns Promise<InvoiceNumber[]> - Array of invoice numbers
   */
  async getInvoiceHistory(companyPrefix: string, limitCount: number = 10): Promise<InvoiceNumber[]> {
    try {
      const invoicesRef = collection(db, 'invoiceNumbers');
      const invoicesQuery = query(
        invoicesRef,
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(invoicesQuery);
      return snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        } as InvoiceNumber))
        .filter(invoice => invoice.companyPrefix === companyPrefix);
    } catch (error) {
      console.error('Error getting invoice history:', error);
      return [];
    }
  }
}

export const invoiceNumberingService = InvoiceNumberingService.getInstance();
export default InvoiceNumberingService;